import type { EnhancedStore } from '@reduxjs/toolkit';
import type { PropsWithChildren } from 'react';
import { Provider as ReduxProvider } from 'react-redux';

import type { ResolvedRoute } from '@routes/types';
import type { PreloadedState } from '@store';
import { DarkModeProvider } from '@components/dark-mode';
import { EntitlementsProvider } from '@components/entitlements';
import { FeatureFlagProvider } from '@components/feature-flag';
import PermissionsProvider from '@components/permissions';
import Router from '@components/router';

import ApolloClientProvider from './apollo-client-provider';
import AppProvider from './app-provider';
import Layout from './layout';
import StyleProvider from './style-provider';

export interface AppShellProps {
  /**
   * Data to initialize the app. To successfully hydrate the client, this data
   * must match what was used on the server - Usually the server should provide
   * this via the window object.
   */
  data: AppData;
  /**
   * A resolved route object to initialize in context. This informs the server
   * render and initial client hydration.
   */
  initialRouteContext: ResolvedRoute;
  /**
   * An array of pre-compiled CSS strings from the server, used during client
   * hydration, after which they are discarded.
   */
  initialStyles?: string[];
  /**
   * The Redux store instance to be provided to the application
   */
  store: EnhancedStore;
  /**
   * Preloaded Redux state from the server that the client store has been
   * initialized with. This is passed along to the Provider to aid stability
   * during client hydration.
   */
  serverState?: PreloadedState;
}

/**
 * The App Shell contains top-level providers and layout for the application,
 * including the HTML document. It represents the structural root before the
 * first Suspense boundary. Rendering the shell should be stable and predictable
 * across server and client to ensure successful hydration on the client. The
 * server starts streaming when the shell is ready, delivering the initial UI.
 */
export const AppShell = ({
  children,
  data,
  initialRouteContext,
  store,
  serverState,
}: PropsWithChildren<AppShellProps>) => (
  <ReduxProvider store={store} serverState={serverState}>
    <AppProvider {...data}>
      <EntitlementsProvider entitlements={data.entitlements}>
        <FeatureFlagProvider flags={data.featureFlags}>
          <Router initialRouteContext={initialRouteContext}>
            <PermissionsProvider>
              <ApolloClientProvider uri={`${data.apiUrl}/reports/graphql`}>
                <StyleProvider>
                  <DarkModeProvider>
                    <Layout>{children}</Layout>
                  </DarkModeProvider>
                </StyleProvider>
              </ApolloClientProvider>
            </PermissionsProvider>
          </Router>
        </FeatureFlagProvider>
      </EntitlementsProvider>
    </AppProvider>
  </ReduxProvider>
);

export default AppShell;
